import { useTranslation } from 'react-i18next';
import { useOneClick } from '../../../contexts/OneClickBuyContext';
import { BumpOffersProps, BumpsProps, BumpType } from '../../../contexts/OneClickBuyContext/types/Bumps';
import { kitOfferPricingKind } from '../../../helpers/combo';
import { useSubscriptionTypes } from '../../../helpers/subscription';
import { convertCentsToBRL } from '../../../utils/currency';
import { ComboContainer } from './Combo';

const Combo = () => {
	const { bumps, currency } = useOneClick();
	const { t } = useTranslation('upsell');
	const subscriptionTypes = useSubscriptionTypes();

	const combos: BumpsProps[] = bumps?.filter(bump => bump.type === BumpType.COMBO) ?? [];

	if (combos.length === 0) return null;

	const { itens_free, items_type } = combos[0];

	const getTitle = () => {
		if (itens_free) return String(t('combo.freeTitle'));
		if (items_type === 'items') return String(t('combo.kitTitle'));
		return String(t('combo.title'));
	};

	const getIntervalLabel = (interval: string | number) =>
		subscriptionTypes.find(type => type.value === Number(interval))?.labelSingular ?? '';

	const renderPricing = (offer: BumpOffersProps, itensFree?: boolean) => {
		const kind = kitOfferPricingKind(offer, itensFree);

		if (kind === 'access') {
			return <small>{String(t('combo.access'))}</small>;
		}

		if (kind === 'freeAccess') {
			return <b className='price'>{String(t('combo.freeAccess'))}</b>;
		}

		const recurringPrice =
			offer.next_charge_price > 0 && offer.next_charge_price !== offer.price
				? offer.next_charge_price
				: offer.price;

		if (kind === 'trial') {
			return (
				<b className='price'>
					{offer.trial_days} {String(t('combo.trialDays'))}, {String(t('combo.then'))}{' '}
					{convertCentsToBRL(recurringPrice, currency)} {getIntervalLabel(offer.interval)}
				</b>
			);
		}

		if (kind === 'nextCharge') {
			return (
				<b className='price'>
					{convertCentsToBRL(offer.price, currency)} {String(t('combo.today'))},{' '}
					{String(t('combo.then'))} {convertCentsToBRL(offer.next_charge_price, currency)}{' '}
					{getIntervalLabel(offer.interval)}
				</b>
			);
		}

		return (
			<b className='price'>
				{convertCentsToBRL(offer.price, currency)}
				{offer.interval ? ` ${getIntervalLabel(offer.interval)}` : ''}
			</b>
		);
	};

	return (
		<ComboContainer>
			<strong>✅ {getTitle()}</strong>

			{combos.map(combo => (
				<div className='combo' key={combo.id}>
					<strong>{combo.title}</strong>

					{items_type !== 'items' ? (
						<div className='pack'>
							{combo.image && <img src={combo.image} alt={combo.title} />}
							{/* Descrição vem em HTML do builder, mesmo padrão do checkout Bolt */}
							<p dangerouslySetInnerHTML={{ __html: combo.description }} />
						</div>
					) : (
						combo.offers.map((offer: BumpOffersProps) => (
							<div className='comboOffer' key={offer.code}>
								<div className='details'>
									{offer.image && (
										<img
											src={offer.image}
											alt={offer.name}
											className={offer.highlight_image ? 'highlight' : undefined}
										/>
									)}
									<div className='info'>
										<strong>{offer.name}</strong>
									</div>
								</div>
								<div className='pricing'>{renderPricing(offer, combo.itens_free)}</div>
							</div>
						))
					)}
				</div>
			))}
		</ComboContainer>
	);
};

export default Combo;
